import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ScanLine, Camera, ImagePlus, Paperclip, Briefcase, Check, Sparkles, CalendarCheck } from "lucide-react";
import ReceiptCrop from "./ReceiptCrop";
import { useStore } from "../context/StoreContext";
import { matchReceipt, ocrImage, parseReceiptText } from "../lib/receiptOcr";
import { fmt, dateLabel } from "../lib/format";
import { staleMissingReceipts, yearEndStatus } from "../lib/finance";
import { shrink } from "./Receipts";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

function Candidates({ list, onAttach, busy }) {
  if (!list.length) return <p className="text-sm text-muted-foreground" data-testid="scan-no-matches">No expense within 25% of that amount and 10 days of that date. Adjust the amount or date above.</p>;
  return (
    <ul className="divide-y rounded-xl border max-h-64 overflow-y-auto" data-testid="scan-matches">
      {list.map(({ tx, exact, needs, learned }) => (
        <li key={tx.id} className="p-2.5 flex items-center gap-3 text-sm" data-testid={`scan-match-${tx.id}`}>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{tx.description}</p>
            <p className="text-xs text-muted-foreground flex flex-wrap gap-1.5 items-center">{dateLabel(tx.date)}{learned && <Badge className="text-[10px]" data-testid={`scan-learned-${tx.id}`}><Sparkles className="h-3 w-3 mr-0.5" />Matched before</Badge>}{exact && <Badge variant="secondary" className="text-[10px]">Exact amount</Badge>}{tx.business && <Badge variant="secondary" className="text-[10px]"><Briefcase className="h-3 w-3 mr-0.5" />Business</Badge>}{needs && <span className="text-warning">needs receipt</span>}{tx.receipts?.length ? <span>· {tx.receipts.length} attached</span> : null}</p>
          </div>
          <span className="num font-semibold">{fmt(Math.abs(tx.amount))}</span>
          <Button size="sm" disabled={busy} onClick={() => onAttach(tx)} data-testid={`scan-attach-${tx.id}`}><Paperclip className="h-3.5 w-3.5 mr-1" />Attach</Button>
        </li>
      ))}
    </ul>
  );
}

function ScanDialog({ onClose }) {
  const { state, addReceipt, learnVendor } = useStore();
  const [raw, setRaw] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(null);
  const [f, setF] = useState({ amount: "", date: "", store: "" });
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const picked = e.target.files?.[0];
    e.target.value = "";
    if (picked) setRaw(await shrink(picked));
  };
  const read = async (img) => {
    setRaw(null);
    setFile(img);
    setPreview(URL.createObjectURL(img));
    setProgress(0);
    try {
      const r = parseReceiptText(await ocrImage(img, setProgress));
      setF({ amount: r.amount ? r.amount.toFixed(2) : "", date: r.date || "", store: r.store || "" });
      if (!r.amount && !r.date) toast("Couldn't read the receipt clearly — type the total and date");
    } catch (err) {
      console.error(err);
      toast.error("Text reading isn't available right now — type the total and date");
    }
    setProgress(null);
  };
  const attach = async (tx) => {
    setBusy(true);
    try {
      await addReceipt(tx.id, file);
      if (f.store.trim()) learnVendor(f.store, tx.description);
      toast.success(`Receipt attached to ${tx.description}`);
      onClose();
    } catch {
      toast.error("Couldn't save the receipt — the device may be out of space");
      setBusy(false);
    }
  };
  const list = file && progress === null ? matchReceipt(state, f) : [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="scan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ScanLine className="h-5 w-5 text-primary" /> Scan a receipt</DialogTitle>
          <DialogDescription>{raw ? "Step 1 of 2 · line up the receipt. " : ""}The photo is read on this device to find the total and date, then matched to your transactions. Nothing is uploaded.</DialogDescription>
        </DialogHeader>
        {raw ? <ReceiptCrop file={raw} onDone={read} /> : !file ? (
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="rounded-2xl border-2 border-dashed p-6 text-center cursor-pointer hover:border-primary transition-colors" data-testid="scan-camera-label"><Camera className="h-8 w-8 mx-auto text-primary" /><p className="font-medium mt-2">Take a photo</p><input type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="scan-camera-input" /></label>
            <label className="rounded-2xl border-2 border-dashed p-6 text-center cursor-pointer hover:border-primary transition-colors" data-testid="scan-upload-label"><ImagePlus className="h-8 w-8 mx-auto text-primary" /><p className="font-medium mt-2">Choose an image</p><input type="file" accept="image/*" className="hidden" onChange={pick} data-testid="scan-file-input" /></label>
          </div>
        ) : (
          <div className="grid sm:grid-cols-[160px_1fr] gap-4">
            <img src={preview} alt="Receipt" className="w-full h-48 sm:h-72 object-contain bg-white rounded-xl border" data-testid="scan-preview" />
            <div className="space-y-3 min-w-0">
              {progress !== null ? (
                <div data-testid="scan-progress"><p className="text-sm font-medium">Reading receipt… {progress}%</p><div className="h-2 rounded-full bg-secondary mt-2 overflow-hidden"><div className="h-full bg-primary transition-[width]" style={{ width: `${progress}%` }} /></div><p className="text-xs text-muted-foreground mt-2">The first scan downloads the reading engine once.</p></div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="col-span-2"><Label>Store (from the receipt header)</Label><Input value={f.store} onChange={(e) => setF({ ...f, store: e.target.value })} placeholder="e.g. OFFICE SUPPLY CO" data-testid="scan-store-input" /></div>
                    <div><Label>Total</Label><Input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="num" data-testid="scan-amount-input" /></div>
                    <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="scan-date-input" /></div>
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Check the numbers, then attach to the right expense.</p>
                  <Candidates list={list} onAttach={attach} busy={busy} />
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ScanReceiptButton({ variant = "outline", className }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant={variant} className={className} onClick={() => setOpen(true)} data-testid="scan-receipt-button"><ScanLine className="h-4 w-4 mr-1.5" /> Scan a receipt</Button>
      {open && <ScanDialog onClose={() => setOpen(false)} />}
    </>
  );
}

export function MissingReceiptAlert() {
  const { state, update } = useStore();
  const stale = staleMissingReceipts(state, 7);
  const seen = new Set(state.receiptAlertDismissed || []);
  if (!stale.some((t) => !seen.has(t.id))) return null;
  return (
    <div className="rounded-2xl border bg-warning-soft border-transparent px-4 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid="missing-receipt-alert">
      <Paperclip className="h-4 w-4 text-warning shrink-0" />
      <span className="flex-1 min-w-[200px]"><b data-testid="missing-receipt-alert-count">{stale.length}</b> business expense{stale.length === 1 ? "" : "s"} older than 7 days {stale.length === 1 ? "has" : "have"} no receipt.</span>
      <ScanReceiptButton variant="secondary" className="h-8" />
      <Button size="sm" variant="outline" className="h-8" asChild><Link to="/reports?tab=tax&missing=1" data-testid="missing-receipt-alert-link">Review</Link></Button>
      <Button size="sm" variant="ghost" className="h-8" onClick={() => update("Dismiss receipt alert", (d) => (d.receiptAlertDismissed = stale.map((t) => t.id)))} data-testid="missing-receipt-alert-dismiss">Dismiss</Button>
    </div>
  );
}

export function YearEndReminder({ extra }) {
  const { state, update } = useStore();
  const s = yearEndStatus(state);
  if (!s) return null;
  return (
    <div className="rounded-2xl border border-primary/30 bg-accent/60 p-4 text-sm" data-testid="year-end-reminder">
      <div className="flex items-start gap-3">
        <CalendarCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold">Get your {s.year} tax pack ready before {dateLabel(s.deadline)} <span className="ml-1 rounded-full bg-background px-2 py-0.5 text-xs num" data-testid="year-end-progress">{s.checklist.done} of {s.checklist.total} done</span></p>
          <ul className="mt-1.5 space-y-1 text-muted-foreground">
            {s.missing.length > 0 && <li data-testid="year-end-missing">• <b className="text-foreground">{s.missing.length}</b> business expense{s.missing.length === 1 ? "" : "s"} without a receipt{s.missing.length ? ` (e.g. ${s.missing.slice(0, 2).map((t) => t.description).join(", ")})` : ""}</li>}
            {s.trips.length > 0 && <li data-testid="year-end-trips">• <b className="text-foreground">{s.trips.length}</b> suggested business trip{s.trips.length === 1 ? "" : "s"} not logged yet</li>}
          </ul>
          <div className="flex flex-wrap gap-2 mt-3">
            {extra}
            {s.missing.length > 0 && <Button size="sm" asChild><Link to={`/reports?tab=tax&year=${s.year}&missing=1`} data-testid="year-end-receipts-link">Review receipts</Link></Button>}
            {s.trips.length > 0 && <Button size="sm" variant="outline" asChild><Link to={`/reports?tab=tax&year=${s.year}#tax-section-mileage`} data-testid="year-end-trips-link">Log trips</Link></Button>}
            <Button size="sm" variant="outline" asChild><Link to={`/reports?tab=tax&year=${s.year}#tax-checklist`} data-testid="year-end-checklist-link">Open checklist</Link></Button>
            <Button size="sm" variant="ghost" onClick={() => update("Dismiss year-end reminder", (d) => (d.yearEndDismissed = s.year))} data-testid="year-end-dismiss">Dismiss for this season</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
